import React, { useEffect } from 'react';
import { Button, Flex, Form, InputNumber, Modal, Select, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getAccounts, getAccountsArray } from 'shared/config/store/selectors/account';
import { useAccountPaymentMutation, useLazyGetAccountsQuery } from 'entities/account/api';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { generalFields } from 'shared/model/schemas/rules';
import { Account } from 'entities/account/model/types';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getSpendableBalance } from 'shared/libs/utils/balance';

const { Text } = Typography;

type MoveFundsModalProps = {
  open: boolean;
  onClose: () => void;
  currentAccountId: number;
};

const MoveFundsModal: React.FC<MoveFundsModalProps> = ({
  open,
  onClose,
  currentAccountId
}) => {
  const { formatMessage: t } = useIntl();
  const [form] = Form.useForm();
  const accounts = useSelector(getAccounts);
  const accountsArray = useSelector(getAccountsArray);
  const currentEmi = useSelector(getCurrentEmi);
  const [accountPayment, { isLoading }] = useAccountPaymentMutation();
  const [refetchAccounts] = useLazyGetAccountsQuery();
  const { showSuccess, showError } = useMessage();
  const rules = generalFields(t);

  useEffect(() => {
    if (open && currentAccountId) {
      form.setFieldsValue({
        accountId: currentAccountId
      });
    }
  }, [open, currentAccountId, form]);

  const handleSubmit = async (values: {
    accountId: number;
    corrAccountId: number;
    amount: number;
  }) => {
    const fromAccount = accounts[String(values.accountId)] as Account | undefined;
    if (!fromAccount) {
      showError(t({ id: 'moveFunds.accountNotFound', defaultMessage: 'Account not found' }));
      return;
    }
    
    if (values.amount > getSpendableBalance(fromAccount.available)) {
      showError(
        t({ id: 'schemas.not.enough', defaultMessage: 'The entered amount exceeds the account balance.' })
      );
      return;
    }

    try {
      await accountPayment({
        accountId: values.accountId,
        amount: values.amount,
        corrAccountId: values.corrAccountId,
        description: ''
      }).unwrap();

      try {
        await refetchAccounts({ emi: currentEmi });
      } catch (e) {
        console.error('Failed to refetch accounts:', e);
      }

      showSuccess(t({ id: 'moveFunds.success', defaultMessage: 'Transfer completed successfully' }));
      form.resetFields();
      onClose();
    } catch (error: any) {
      showError(
        error?.data?.message || 
        t({ id: 'moveFunds.error', defaultMessage: 'Failed to complete transfer. Please try again.' })
      );
    }
  };

  const fromAccount = accounts[String(form.getFieldValue('accountId'))] as Account | undefined;

  return (
    <Modal
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t({ id: 'moveFunds.title', defaultMessage: 'Move funds' })}
        </Typography.Title>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          accountId: currentAccountId
        }}
      >
        <Form.Item
          name="accountId"
          label={t({ id: 'moveFunds.fromAccount', defaultMessage: 'From account' })}
          rules={[{ required: true, message: t({ id: 'schemas.new.required-field' }) }]}
        >
          <Select
            placeholder={t({ id: 'moveFunds.selectAccount', defaultMessage: 'Select account' })}
            onChange={() => {
              form.setFieldValue('amount', undefined);
            }}
          >
            {accountsArray.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                <Flex justify="space-between" align="center">
                  <Text>{account.title || account.iban}</Text>
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    {getSpendableBalance(account.available).toFixed(2)} €
                  </Text>
                </Flex>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="corrAccountId"
          label={t({ id: 'moveFunds.toAccount', defaultMessage: 'To account' })}
          rules={[
            { required: true, message: t({ id: 'schemas.new.required-field' }) },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('accountId') !== value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t({ id: 'moveFunds.sameAccountError', defaultMessage: 'From and to accounts must be different' }))
                );
              }
            })
          ]}
        >
          <Select
            placeholder={t({ id: 'moveFunds.selectAccount', defaultMessage: 'Select account' })}
          >
            {accountsArray.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                <Flex justify="space-between" align="center">
                  <Text>{account.title || account.iban}</Text>
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    {getSpendableBalance(account.available).toFixed(2)} €
                  </Text>
                </Flex>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="amount"
          label={t({ id: 'moveFunds.amount', defaultMessage: 'Amount' })}
          rules={[
            ...rules.amount,
            { required: true, message: t({ id: 'schemas.new.required-field' }) }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="0.00"
            min={0.01}
            step={0.01}
            precision={2}
            prefix="€"
          />
        </Form.Item>

        {fromAccount && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t({ id: 'moveFunds.available', defaultMessage: 'Available' })}: {getSpendableBalance(fromAccount.available).toFixed(2)} €
          </Text>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isLoading}
            style={{ height: 44 }}
          >
            {t({ id: 'buttons.submit', defaultMessage: 'Submit' })}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MoveFundsModal;

